import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import {
  CLARI_CHAT_MODEL_VENDOR_BY_ID,
  getClariChatModelsFlat
} from 'src/app/shared/clari-chat-model-catalog';

interface GardenModel {
  name: string;
  modelId: string;
  provider: string;
  description: string;
  contextWindow: string;
}

interface ModelCategory {
  id: string;
  title: string;
  description: string;
  models: GardenModel[];
}

@Component({
  selector: 'app-model-garden',
  templateUrl: './model-garden.component.html',
  styleUrls: ['./model-garden.component.scss']
})
export class ModelGardenComponent implements OnInit {
  readonly modelsPerSlide = 4;

  filtersForm = new FormGroup({
    search: new FormControl('')
  });

  categories: ModelCategory[] = [
    {
      id: 'reasoning-vision',
      title: 'Reasoning Models (Vision)',
      description: 'Visual-capable reasoning models for image+text understanding, multimodal analysis, and grounded visual responses.',
      models: [
        {
          name: 'Claude Haiku 4.5',
          modelId: 'haiku45',
          provider: 'Anthropic',
          description: 'Claude Haiku 4.5 is optimized for fast multimodal reasoning and concise, reliable visual-text responses in day-to-day production workflows.',
          contextWindow: '200k'
        },
        {
          name: 'Claude Sonnet 4.5',
          modelId: 'sonnet45',
          provider: 'Anthropic',
          description: 'Claude Sonnet 4.5 balances strong vision-language reasoning, coding support, and instruction fidelity for complex multimodal tasks.',
          contextWindow: '200k'
        },
        {
          name: 'Palmyra X5',
          modelId: 'palmx',
          provider: 'Writer',
          description: 'Palmyra X5 is an enterprise model optimized for 1M-token context and agentic tool use across large documents. It is built for consistent, governed workflows that require structured outputs.',
          contextWindow: '1M'
        },
        {
          name: 'Llama 4',
          modelId: 'maverick',
          provider: 'Meta',
          description: 'Llama 4 is an open-weight MoE model built for frontier reasoning, multimodal understanding, and massive context windows. It is designed for scalable enterprise deployments and customizable workflows.',
          contextWindow: '128k'
        }
      ]
    },
    {
      id: 'reasoning-text',
      title: 'Reasoning Models (Text)',
      description: 'Text-first reasoning models for long-chain analysis, coding, enterprise writing, and multi-step decision support.',
      models: [
        {
          name: 'ChatGPT 5.4',
          modelId: 'chatgpt54',
          provider: 'OpenAI',
          description: 'Flagship reasoning model with strong coding and analysis.',
          contextWindow: '256k'
        },
        {
          name: 'Claude Opus 4.6',
          modelId: 'opus46',
          provider: 'Anthropic',
          description: 'Claude Opus 4.6 is Anthropic’s flagship frontier model built for deep reasoning, long-context analysis, and elite coding reliability. It excels on high-stakes tasks where accuracy, nuance, and sustained coherence are essential.',
          contextWindow: '200k'
        },
        {
          name: 'GLM 4.7',
          modelId: 'zai',
          provider: 'Z.ai',
          description: 'GLM 4.7 is a high-performance bilingual model designed for fast structured reasoning, long-context intelligence, and tool-enabled workflows. It balances speed with strong coding and planning capabilities for enterprise use.',
          contextWindow: '128k'
        },
        {
          name: 'Palmyra X5',
          modelId: 'palmx',
          provider: 'Writer',
          description: 'Palmyra X5 is an enterprise model optimized for 1M-token context and agentic tool use across large documents. It is built for consistent, governed workflows that require structured outputs.',
          contextWindow: '1M'
        },
        {
          name: 'Jamba Large',
          modelId: 'jamba',
          provider: 'AI21 Labs',
          description: 'Jamba Large is unique for its hybrid SSM-plus-Transformer architecture, which helps it handle very long prompts and ongoing chat histories with better speed-to-quality efficiency than many dense-only models. In ClariChat it is a strong choice when you need reliable multi-turn memory and structured outputs.',
          contextWindow: '256k'
        },
        {
          name: 'Mistral Large',
          modelId: 'mistrallarge',
          provider: 'Mistral AI',
          description: 'Large enterprise-grade model for serious workloads.',
          contextWindow: '128k'
        },
        {
          name: 'Llama 4',
          modelId: 'llama4',
          provider: 'Meta',
          description: 'Llama 4 is a next-generation open-weight reasoning model focused on scalable enterprise performance, high-context dialogue, and adaptable deployment across private and cloud stacks.',
          contextWindow: '128k'
        }
      ]
    },
    {
      id: 'instruct-models',
      title: 'Instruct Models',
      description: 'Instruction-tuned models for direct task execution in concise, enterprise-ready chat workflows.',
      models: [
        {
          name: 'Meta Scout',
          modelId: 'scoutInstruct',
          provider: 'Meta',
          description: 'Meta Scout Instruct is optimized for low-latency chat and practical instruction following in serverless Bedrock environments. It is a strong fit for lightweight copilots and responsive enterprise assistants.',
          contextWindow: '128k'
        },
        {
          name: 'Command R Instruct',
          modelId: 'commandRInstruct',
          provider: 'Cohere',
          description: 'Cohere Command R Instruct is an instruction-tuned model oriented toward enterprise chat, retrieval-oriented workflows, and consistent professional tone. It performs well in business communication and structured response tasks.',
          contextWindow: '128k'
        },
        {
          name: 'DeepSeek R1',
          modelId: 'deepseek',
          provider: 'DeepSeek',
          description: 'Deepseek R1 is a reasoning-first model optimized for technical precision and code-centric problem solving. It prioritizes deterministic, structured answers with clear logic.',
          contextWindow: '128k'
        },
        {
          name: 'Cohere R+',
          modelId: 'cohere',
          provider: 'Cohere',
          description: 'Cohere R+ is an enterprise-grade model built for reliable, responsible language understanding and retrieval-driven tasks. It is designed for governed production use with strong safety controls.',
          contextWindow: '128k'
        }
      ]
    },
    {
      id: 'video-models',
      title: 'Video & Image Models',
      description: 'Video and image generation models from Atomic Studio for cinematic scenes, fast ideation, and visual experimentation.',
      models: [
        {
          name: 'Veo 3.1',
          modelId: 'veo31',
          provider: 'Google',
          description: 'Veo 3.1 is a cinematic text-to-video model designed for smooth camera movement, coherent motion, and film-style scene composition.',
          contextWindow: 'Prompt-based'
        },
        {
          name: 'CogVideoX-3',
          modelId: 'cogvideox3',
          provider: 'CogX',
          description: 'CogVideoX-3 (Z.ai) is a text-to-video model that can produce bold, stylized motion and is often used for creative exploration. It is useful when you’re iterating on dynamic visuals and expressive movement.',
          contextWindow: 'Prompt-based'
        },
        {
          name: 'Nova Reel 1.1',
          modelId: 'novareel11',
          provider: 'Amazon',
          description: 'Nova Reel 1.1 is optimized for long-form prompt-to-video generation and optional image-primed scenes, with server-side Bedrock async polling handled before returning the final video URL.',
          contextWindow: 'Prompt-based'
        },
        {
          name: 'Gemini Pro Image',
          modelId: 'geminiproimage',
          provider: 'Google',
          description: 'Gemini Pro Image is built for high-quality prompt-to-image generation and visual concept exploration with strong instruction following.',
          contextWindow: 'Prompt-based'
        },
        {
          name: 'Stability 3.5',
          modelId: 'stability35l',
          provider: 'Stability AI',
          description: 'Stability 3.5 is an image model optimized for prompt-accurate generation, detailed visuals, and reference-guided creative workflows.',
          contextWindow: 'Prompt-based'
        }
      ]
    }
  ];

  selectedCategoryId = this.categories[0].id;
  filteredCategories: ModelCategory[] = this.categories;
  categorySlideIndex: Record<string, number> = {};

  constructor(private router: Router) {}

  get selectedCategory(): ModelCategory | undefined {
    return this.filteredCategories.find((category) => category.id === this.selectedCategoryId) ?? this.filteredCategories[0];
  }

  ngOnInit(): void {
    this.syncFirstThreeCardsWithCatalog();
    this.initializeSliderState(this.categories);
    this.filtersForm.valueChanges.subscribe((value) => this.applySearch(value.search || ''));
  }

  search(value: string): void {
    this.filtersForm.patchValue({ search: value || '' }, { emitEvent: true });
  }

  selectCategory(categoryId: string): void {
    this.selectedCategoryId = categoryId;
  }

  visibleModels(category: ModelCategory): GardenModel[] {
    return category.models;
  }

  hasPrev(category: ModelCategory): boolean {
    void category;
    return false;
  }

  hasNext(category: ModelCategory): boolean {
    void category;
    return false;
  }

  slideNext(category: ModelCategory): void {
    void category;
  }

  slidePrev(category: ModelCategory): void {
    void category;
  }

  private initializeSliderState(categories: ModelCategory[]): void {
    categories.forEach((category) => {
      if (this.categorySlideIndex[category.id] === undefined) {
        this.categorySlideIndex[category.id] = 0;
      }
    });
  }

  private applySearch(query: string): void {
    const normalized = query.trim().toLowerCase();

    if (!normalized) {
      this.filteredCategories = this.categories;
      this.initializeSliderState(this.filteredCategories);
      if (!this.filteredCategories.find((category) => category.id === this.selectedCategoryId)) {
        this.selectedCategoryId = this.filteredCategories[0]?.id || '';
      }
      return;
    }

    this.filteredCategories = this.categories
      .map((category) => ({
        ...category,
        models: category.models.filter((model) => {
          const haystack = `${model.name} ${model.modelId} ${model.provider} ${model.description} ${category.title}`.toLowerCase();
          return haystack.includes(normalized);
        })
      }))
      .filter((category) => category.models.length > 0);

    this.filteredCategories.forEach((category) => {
      this.categorySlideIndex[category.id] = 0;
    });

    if (!this.filteredCategories.find((category) => category.id === this.selectedCategoryId)) {
      this.selectedCategoryId = this.filteredCategories[0]?.id || '';
    }
  }

  canOpenInClariStudio(model: GardenModel, category: ModelCategory): boolean {
    if (category.id !== 'video-models') return false;
    return ['veo31', 'novareel11', 'stability35l', 'geminiproimage'].includes(model.modelId);
  }

  openInClariStudio(model: GardenModel): void {
    this.router.navigate(['/ai/videos'], {
      queryParams: { selectedModel: model.modelId }
    });
  }

  private syncFirstThreeCardsWithCatalog(): void {
    const catalogModels = getClariChatModelsFlat();
    const firstThree = this.categories.slice(0, 3);
    const existingIds = new Set(firstThree.flatMap((category) => category.models.map((model) => model.modelId)));
    const missing = catalogModels.filter((model) => !existingIds.has(model.id));

    missing.forEach((model) => {
      const target = this.pickTargetCategoryForModel(model.id);
      target.models.push({
        name: model.name,
        modelId: model.id,
        provider: this.providerForModelId(model.id),
        description: `${model.name} is available in Clari Chat with model-id linking support.`,
        contextWindow: 'N/A'
      });
    });
  }

  private pickTargetCategoryForModel(modelId: string): ModelCategory {
    const id = String(modelId || '').toLowerCase();
    const visionCard = this.categories[0];
    const reasoningCard = this.categories[1];
    const instructCard = this.categories[2];

    if (['haiku45', 'pixtralimage', 'gemini', 'gemini3flash', 'chatgpt54', 'chatgpt5', 'minichatgpt54'].includes(id)) {
      return visionCard;
    }
    if (id.endsWith('instruct')) {
      return instructCard;
    }
    return reasoningCard;
  }

  private providerForModelId(modelId: string): string {
    const vendor = CLARI_CHAT_MODEL_VENDOR_BY_ID[String(modelId || '').toLowerCase()] || '';
    const byVendor: Record<string, string> = {
      anthropic: 'Anthropic',
      openai: 'OpenAI',
      google: 'Google',
      mistral: 'Mistral',
      writer: 'Writer',
      deepseek: 'DeepSeek',
      cohere: 'Cohere',
      meta: 'Meta',
      ai21: 'AI21 Labs',
      amazon: 'Amazon'
    };
    return byVendor[vendor] || 'AI Model';
  }
}
